<template>
  <div class="filter-panel-wrapper" :style="filterPanelStyle">
    <div 
      class="filter-panel" 
      :class="{ collapsed: !panelExpanded, expanded: panelExpanded }"
    >
      <!-- Header - Entire area is clickable -->
      <div class="filter-header" @click="togglePanel">
        <div class="header-content">
          <div class="header-text">
            <h3>Filter</h3>
            <p v-if="panelExpanded">Select your filters below</p>
            <p v-else class="expand-hint">Click to expand and filter</p>
          </div>
          <div class="toggle-icon">
            <span class="expand-arrow" v-if="!panelExpanded">Filter ▶</span>
            <span class="expand-arrow" v-else>◀</span>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div v-if="panelExpanded" class="filter-content">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading filters...</p>
        </div>

        <!-- Filter Tree -->
        <div v-else class="filter-tree">

          <!-- <div class="search-section" style="padding: 10px;">
            <div class="search-bar">
              <label class="options-label" for="respondentSearch">Respondent Search:</label>
              <input id="searchQuery" type="text" class="search-input" v-model="searchQuery"
                minlength="3" placeholder="Type at least 3 chars..."
                @keydown.enter.stop.prevent="handleSearch" />
              <Button label="Search" id="batchAction" @click.prevent="handleSearch"
                :disabled="disableSearchButton">
              </Button>
            </div>
          </div> -->

          <!-- Organizations -->
          <div class="tree-item">
            <div class="tree-node" @click="toggleExpanded('organizations')">
              <div class="node-content">
                <span class="node-label">Organizations</span>
                <span class="node-count">{{ organizations.length }}</span>
              </div>
              <span class="expand-icon" :class="{ expanded: expanded.organizations }">▼</span>
            </div>
            <div v-if="expanded.organizations" class="tree-children">
              <div 
                v-for="org in organizations" 
                :key="org.id" 
                class="child-node"
                :class="{ selected: selectedFilters.organization?.id === org.id }"
                @click="selectFilter('organization', org)"
              >
                <div class="child-content">
                  <input 
                    type="checkbox" 
                    class="checkbox" 
                    :checked="selectedFilters.organization?.id === org.id"
                    @click.stop
                  >
                  <span>{{ org.name }}</span>
                </div>
                <span class="node-count">{{ org.subOrgs?.length || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Sub-Organizations -->
          <div v-if="selectedFilters.organization" class="tree-item">
            <div class="tree-node" @click="toggleExpanded('subOrganizations')">
              <div class="node-content">
                <span class="node-label">Sub-Organizations</span>
                <span class="node-count">{{ selectedFilters.organization.subOrgs?.length || 0 }}</span>
              </div>
              <span class="expand-icon" :class="{ expanded: expanded.subOrganizations }">▼</span>
            </div>
            <div v-if="expanded.subOrganizations" class="tree-children">
              <div 
                v-for="suborg in selectedFilters.organization.subOrgs" 
                :key="suborg.id"
                class="child-node"
                :style="{ 
                  borderLeftColor: primaryColor 
                }"
                :class="{ selected: selectedFilters.subOrganization?.id === suborg.id }"
                @click="selectFilter('subOrganization', suborg)"
              >
                <div class="child-content">
                  <input 
                    type="checkbox" 
                    class="checkbox"
                    :checked="selectedFilters.subOrganization?.id === suborg.id"
                    @click.stop
                  >
                  <span>{{ suborg.name }}</span>
                </div>
                <span class="node-count">{{ suborg.programs?.length || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Programs -->
          <div v-if="selectedFilters.subOrganization" class="tree-item">
            <div class="tree-node" @click="toggleExpanded('programs')">
              <div class="node-content">
                <span class="node-label">Programs</span>
                <span class="node-count">{{ selectedFilters.subOrganization.programs?.length || 0 }}</span>
              </div>
              <span class="expand-icon" :class="{ expanded: expanded.programs }">▼</span>
            </div>
            <div v-if="expanded.programs" class="tree-children">
              <div 
                v-for="program in selectedFilters.subOrganization.programs"
                :key="program.id"
                class="child-node"
                :class="{ selected: selectedFilters.program?.id === program.id }"
                @click="selectFilter('program', program)"
              >
                <div class="child-content">
                  <input 
                    type="checkbox" 
                    class="checkbox"
                    :checked="selectedFilters.program?.id === program.id"
                    @click.stop
                  >
                  <span>{{ program.name }}</span>
                </div>
                <span class="node-count">{{ program.iterations?.length || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Iterations -->
          <div v-if="selectedFilters.program" class="tree-item">
            <div class="tree-node" @click="toggleExpanded('iterations')">
              <div class="node-content">
                <span class="node-label">Iterations</span>
                <span class="node-count">{{ selectedFilters.program.iterations?.length || 0 }}</span>
              </div>
              <span class="expand-icon" :class="{ expanded: expanded.iterations }">▼</span>
            </div>
            <div v-if="expanded.iterations" class="tree-children">
              <div 
                v-for="iteration in selectedFilters.program.iterations"
                :key="iteration.id"
                class="child-node"
                :class="{ selected: selectedFilters.iteration?.id === iteration.id }"
                @click="selectFilter('iteration', iteration)"
              >
                <div class="child-content">
                  <input 
                    type="checkbox" 
                    class="checkbox"
                    :checked="selectedFilters.iteration?.id === iteration.id"
                    @click.stop
                  >
                  <span>{{ iteration.name }}</span>
                </div>
                <span class="node-count">{{ iteration.streams?.length || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Streams -->
          <div v-if="selectedFilters.iteration" class="tree-item">
            <div class="tree-node" @click="toggleExpanded('streams')">
              <div class="node-content">
                <span class="node-label">Streams</span>
                <span class="node-count">{{ selectedFilters.iteration.streams?.length || 0 }}</span>
              </div>
              <span class="expand-icon" :class="{ expanded: expanded.streams }">▼</span>
            </div>
            <div v-if="expanded.streams" class="tree-children">
              <div 
                v-for="stream in selectedFilters.iteration.streams"
                :key="stream.id"
                class="child-node"
                :class="{ selected: selectedFilters.stream?.id === stream.id }"
                @click="selectFilter('stream', stream)"
              >
                <div class="child-content">
                  <input 
                    type="checkbox" 
                    class="checkbox"
                    :checked="selectedFilters.stream?.id === stream.id"
                    @click.stop
                  >
                  <span>{{ stream.name }}</span>
                </div>
                <span class="node-count">{{ stream.groups?.length || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Groups -->
          <div v-if="selectedFilters.stream" class="tree-item">
            <div class="tree-node" @click="toggleExpanded('groups')">
              <div class="node-content">
                <span class="node-label">Groups</span>
                <span class="node-count">{{ selectedFilters.stream.groups?.length || 0 }}</span>
              </div>
              <span class="expand-icon" :class="{ expanded: expanded.groups }">▼</span>
            </div>
            <div v-if="expanded.groups" class="tree-children">
              <div 
                v-for="group in selectedFilters.stream.groups"
                :key="group.id"
                class="child-node"
                :class="{ selected: selectedFilters.group?.id === group.id }"
                @click="selectFilter('group', group)"
              >
                <div class="child-content">
                  <input 
                    type="checkbox" 
                    class="checkbox"
                    :checked="selectedFilters.group?.id === group.id"
                    @click.stop
                  >
                  <span>{{ group.name }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Respondent Search -->
          <div class="search-section">
            <label class="search-label">Respondent Search:</label>
            <input 
              type="text" 
              class="search-input" 
              v-model="searchQuery"
              placeholder="Type at least 3 chars..."
              @keydown.enter="handleSearch"
              :style="{ 
                borderColor: searchQuery.length >= 3 ? secondaryColor : '#ced4da' 
              }"
            >
          </div>
        </div>

        <!-- Filter Footer -->
        <div v-if="hasSelections" class="filter-footer">
          <div class="filter-summary">
            <div class="selected-count">
              {{ selectionCount }} filter{{ selectionCount !== 1 ? 's' : '' }} selected
            </div>
            <div class="breadcrumb">
              <span v-if="selectedFilters.organization">
                {{ selectedFilters.organization.name }}
              </span>
              <span v-if="selectedFilters.subOrganization">
                → {{ selectedFilters.subOrganization.name }}
              </span>
              <span v-if="selectedFilters.program">
                → {{ selectedFilters.program.name }}
              </span>
              <span v-if="selectedFilters.iteration">
                → {{ selectedFilters.iteration.name }}
              </span>
              <span v-if="selectedFilters.stream">
                → {{ selectedFilters.stream.name }}
              </span>
              <span v-if="selectedFilters.group">
                → {{ selectedFilters.group.name }}
              </span>
            </div>
          </div>
          <div class="action-buttons">
            <button 
              class="btn-apply" 
              @click="applyFilters" 
              :disabled="!canApply"
              :style="{
                backgroundColor: secondaryColor,
                color: buttonTextColor
              }"
            >
              Search
            </button>
            <button 
              class="btn-clear" 
              @click="clearAllFilters"
              :style="{
                backgroundColor: secondaryColor,
                color: buttonTextColor
              }"
            >
              Clear all filters
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReportFilterPanel',
  props: {
      organizations: {
        type: Array,
        required: true,
        default: () => []
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      wrapperTop: {
        type: Number,
        default: 0
      },
      brandData: {
        type: Object,
        default: () => ({})
      }
    },
  data() {
    return {
      panelExpanded: false,
      searchQuery: '',
      selectedFilters: {
        organization: null,
        subOrganization: null,
        program: null,
        iteration: null,
        stream: null,
        group: null
      },
      expanded: {
        organizations: false,
        subOrganizations: false,
        programs: false,
        iterations: false,
        streams: false,
        groups: false
      }
    };
  },
  computed: {
    filterPanelStyle() {
      var topPosition = 70;
      return {
        top: `${topPosition}px`
      };
    },
    hasSelections() {
      return Object.values(this.selectedFilters).some(v => v !== null) || 
            this.searchQuery.length >= 3;
    },
    selectionCount() {
      return Object.values(this.selectedFilters).filter(v => v !== null).length;
    },
    canApply() {
      return this.hasSelections;
    },
    primaryColor() {
      return this.brandData?.main_color1 || '#0e5071';
    },
    secondaryColor() {
      return this.brandData?.main_color2 || '#0a3a52';
    },
    buttonTextColor() {
      return this.brandData?.button_text_color || '#ffffff';
    }
  },
  methods: {
    togglePanel() {
      this.panelExpanded = !this.panelExpanded;
    },
    toggleExpanded(section) {
      this.expanded[section] = !this.expanded[section];
    },
    selectFilter(type, value) {
      const hierarchy = [
        'organization', 
        'subOrganization', 
        'program', 
        'iteration', 
        'stream', 
        'group'
      ];
      const currentIndex = hierarchy.indexOf(type);
      
      if (this.selectedFilters[type]?.id === value.id) {
        this.selectedFilters[type] = null;
      } else {
        this.selectedFilters[type] = value;
      }
      
      for (let i = currentIndex + 1; i < hierarchy.length; i++) {
        this.selectedFilters[hierarchy[i]] = null;
      }

      this.$emit('filter-changed', this.getFilterData());
    },
    clearAllFilters() {
      this.selectedFilters = {
        organization: null,
        subOrganization: null,
        program: null,
        iteration: null,
        stream: null,
        group: null
      };
      this.searchQuery = '';
      this.$emit('filters-cleared');
      this.$emit('filter-changed', this.getFilterData());
    },
    applyFilters() {
      const filterData = this.getFilterData();
      this.$emit('apply-filters', filterData);
    },
    handleSearch() {
      if (this.searchQuery.length >= 3) {
        this.applyFilters();
      } else {
        alert('Please type at least 3 characters for respondent search');
      }
    },
    getFilterData() {
      return {
        organization: this.selectedFilters.organization,
        subOrganization: this.selectedFilters.subOrganization,
        program: this.selectedFilters.program,
        iteration: this.selectedFilters.iteration,
        stream: this.selectedFilters.stream,
        group: this.selectedFilters.group,
        searchQuery: this.searchQuery.length >= 3 ? this.searchQuery : null
      };
    }
  }
};
</script>

<style scoped>
.filter-panel-wrapper {
  position: fixed;
  left: 0;
  z-index: 998;
  max-height: 60vh;
  opacity: 1;
  pointer-events: auto;
  transition: top 0.2s ease, opacity 0.3s ease;
}

.filter-panel {
  background: white;
  box-shadow: 2px 0 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 60vh;
}

.filter-panel.collapsed {
  width: 50px;
}

.filter-panel.expanded {
  width: 350px;
}

/* Header - Make entire area clickable */
.filter-header {
  padding: 15px;
  background: transparent;
  border-bottom: 1px solid #dee2e6;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
  transition: background 0.2s;
  z-index: 998;
}

.filter-header:hover {
  background: rgba(0, 0, 0, 0.02);
}

.collapsed .filter-header {
  padding: 15px 12px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-text {
  flex: 1;
}

.header-text h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.header-text p {
  margin: 0;
  font-size: 12px;
  color: #666;
}

.expand-hint {
  color: #888;
  font-style: italic;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.collapsed .header-text {
  display: none;
}

.toggle-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba(0,0,0,0.05);
  transition: all 0.2s;
  margin-left: 8px;
}

.filter-header:hover .toggle-icon {
  background: rgba(0,0,0,0.1);
  transform: scale(1.1);
}

.expand-arrow {
  font-size: 12px;
  color: v-bind(primaryColor);
  transition: transform 0.3s;
  font-weight: bold;
}

.filter-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
}

.loading-state {
  padding: 40px 20px;
  text-align: center;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 15px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid v-bind(primaryColor);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.filter-tree {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 10px;
}

.tree-item {
  border-bottom: 1px solid #f0f0f0;
}

.tree-node {
  padding: 12px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.2s;
}

.tree-node:hover {
  background: #f8f9fa;
}

.node-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.node-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.node-count {
  background: #e9ecef;
  color: #6c757d;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
}

.expand-icon{
 color: v-bind(primaryColor);
}

.tree-node .expand-icon {
  transition: transform 0.2s;
}

.tree-node .expand-icon.expanded {
  transform: rotate(180deg);
}

.tree-children {
  background: #fafbfc;
  border-left: 3px solid #e9ecef;
}

.child-node {
  padding: 10px 15px 10px 35px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.2s;
}

.child-node:hover {
  background: #f1f3f4;
}

.child-node.selected {
  background: #e3f2fd;
  border-left: 3px solid v-bind(primaryColor);
}

.child-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.child-content span {
  font-size: 13px;
  color: #555;
}

.child-node.selected .child-content span {
  color: v-bind(primaryColor);
  font-weight: 500;
}

.checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: v-bind(primaryColor);
}

.search-section {
  padding: 15px;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
}

.search-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: v-bind(primaryColor);
}

.filter-footer {
  padding: 15px;
  border-top: 1px solid #dee2e6;
  background: #fff;
  flex-shrink: 0;
}

.filter-summary {
  margin-bottom: 12px;
}

.selected-count {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  margin-bottom: 6px;
}

.breadcrumb {
  font-size: 11px;
  color: #666;
  line-height: 1.6;
  font-weight: bold;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-apply,
.btn-clear {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-apply {
  background: v-bind(primaryColor);
  color: white;
}

.btn-apply:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-apply:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-clear {
  background: #f8f9fa;
  color: #666;
  border: 1px solid #dee2e6;
}

.btn-clear:hover {
  opacity: 0.8;
  transform: translateY(-1px);
}

.filter-tree::-webkit-scrollbar {
  width: 6px;
}

.filter-tree::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.filter-tree::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.filter-tree::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>